import { useEffect, useMemo, useState } from "react";
import api, { formatApiError, API_BASE } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import { Upload, Plus, Signal, Search, X, Download, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { formatDate, relTime } from "../lib/date";

const STATUS_OPTIONS = [
  { v: "", label: "All statuses" },
  { v: "new", label: "New" },
  { v: "not_connected", label: "Not Connected" },
  { v: "completed", label: "Completed" },
  { v: "hold", label: "Hold" },
  { v: "rejected", label: "Rejected" },
  { v: "follow_up", label: "Follow-up" },
];

export default function AdminLeads() {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [telecallers, setTelecallers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [filter, setFilter] = useState({ status: "", assigned_to: "", search: "" });
  const [showAdd, setShowAdd] = useState(false);
  const [assignTo, setAssignTo] = useState("");
  const [detailLead, setDetailLead] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // { ids: string[], label: string }

  const load = () => {
    const params = {};
    if (filter.status) params.status = filter.status;
    if (filter.assigned_to) params.assigned_to = filter.assigned_to;
    if (filter.search) params.search = filter.search;
    api.get("/leads", { params }).then((r) => setLeads(r.data));
  };

  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [filter, user]);
  useEffect(() => { if (user) api.get("/users/telecallers").then((r) => setTelecallers(r.data)); }, [user]);

  const toggle = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === leads.length) setSelected(new Set());
    else setSelected(new Set(leads.map(l => l.id)));
  };

  const assign = async () => {
    if (!assignTo || selected.size === 0) return toast.error("Pick a telecaller and select leads");
    try {
      await api.post("/leads/assign", { lead_ids: [...selected], telecaller_id: assignTo });
      toast.success(`Assigned ${selected.size} leads`);
      setSelected(new Set()); setAssignTo(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendSignal = async () => {
    if (selected.size === 0) return toast.error("Select leads first");
    try {
      const { data } = await api.post("/leads/quality-signal", { lead_ids: [...selected], quality: true });
      toast.success(`Quality signal → ${data.updated} leads`, { description: data.note });
      setSelected(new Set()); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const downloadFile = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      if (deleteTarget.ids.length === 1) {
        await api.delete(`/leads/${deleteTarget.ids[0]}`);
      } else {
        await api.post("/leads/bulk-delete", { lead_ids: deleteTarget.ids });
      }
      toast.success(`Deleted ${deleteTarget.ids.length} lead${deleteTarget.ids.length > 1 ? "s" : ""}`);
      setDeleteTarget(null);
      setSelected(new Set());
      setDetailLead(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const uploadCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/leads/upload-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.inserted} leads`); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    e.target.value = "";
  };

  return (
    <div className="px-4 sm:px-8 pb-8">
      <div className="sticky top-[52px] lg:top-0 z-30 -mx-4 sm:-mx-8 px-4 sm:px-8 pt-4 sm:pt-8 pb-3 bg-[#F4F4F5] border-b border-zinc-200/50">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="mb-1 text-[11px] uppercase tracking-widest text-zinc-500">Pipeline</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-black">Leads</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button data-testid="csv-template-link" onClick={() => downloadFile("/leads-template.csv", "waleads-template.csv")}
             className="inline-flex items-center gap-1.5 rounded-sm border border-zinc-300 bg-white px-3 py-2 text-xs font-medium hover:bg-zinc-50">
            <Download className="h-3.5 w-3.5" /> Template
          </button>
          <button data-testid="offline-events-download" onClick={() => downloadFile("/leads-offline-events.csv", "meta-offline-events.csv")}
             className="inline-flex items-center gap-1.5 rounded-sm border border-emerald-500 bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-700 hover:bg-emerald-100">
            <Download className="h-3.5 w-3.5" /> Meta Offline Events CSV
          </button>
          <label data-testid="upload-csv-btn" className="cursor-pointer inline-flex items-center gap-1.5 rounded-sm border border-zinc-300 bg-white px-3 py-2 text-xs font-medium hover:bg-zinc-50">
            <Upload className="h-3.5 w-3.5" /> Upload CSV
            <input type="file" accept=".csv" className="hidden" onChange={uploadCsv} data-testid="csv-file-input" />
          </label>
          <button data-testid="add-lead-btn" onClick={() => setShowAdd(true)} className="inline-flex items-center gap-1.5 rounded-sm bg-black px-3 py-2 text-xs font-medium text-white hover:bg-zinc-800">
            <Plus className="h-3.5 w-3.5" /> Add Lead
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 rounded-sm border border-zinc-200 bg-white p-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-3.5 w-3.5 text-zinc-400" />
          <input data-testid="search-input" placeholder="Search name / phone / campaign" value={filter.search}
            onChange={(e) => setFilter({ ...filter, search: e.target.value })}
            className="w-64 rounded-sm border border-zinc-300 bg-white pl-8 pr-3 py-1.5 text-xs outline-none focus:border-black" />
        </div>
        <select data-testid="filter-status" value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })}
          className="rounded-sm border border-zinc-300 bg-white px-2 py-1.5 text-xs outline-none focus:border-black">
          {STATUS_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
        </select>
        <select data-testid="filter-assigned" value={filter.assigned_to} onChange={(e) => setFilter({ ...filter, assigned_to: e.target.value })}
          className="rounded-sm border border-zinc-300 bg-white px-2 py-1.5 text-xs outline-none focus:border-black">
          <option value="">All telecallers</option>
          <option value="unassigned">Unassigned</option>
          {telecallers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>

        <div className="ml-auto flex items-center gap-2">
          <span className="text-[11px] text-zinc-500">{selected.size} selected</span>
          <select data-testid="assign-telecaller-select" value={assignTo} onChange={(e) => setAssignTo(e.target.value)}
            className="rounded-sm border border-zinc-300 bg-white px-2 py-1.5 text-xs outline-none focus:border-black">
            <option value="">Assign to…</option>
            {telecallers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <button data-testid="assign-btn" onClick={assign} disabled={!selected.size || !assignTo}
            className="rounded-sm bg-black px-3 py-1.5 text-xs font-medium text-white hover:bg-zinc-800 disabled:opacity-50">
            Assign
          </button>
          <button data-testid="meta-signal-trigger" onClick={sendSignal} disabled={!selected.size}
            className="group inline-flex items-center gap-1.5 rounded-sm border border-black bg-white px-3 py-1.5 text-xs font-medium text-black hover:bg-zinc-50 disabled:opacity-50">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75"/>
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500"/>
            </span>
            <Signal className="h-3.5 w-3.5" />
            Meta Quality Signal
          </button>
          <button data-testid="bulk-delete-btn" onClick={() => setDeleteTarget({ ids: [...selected], label: `${selected.size} selected leads` })} disabled={!selected.size}
            className="inline-flex items-center gap-1.5 rounded-sm border border-red-500 bg-white px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50">
            <Trash2 className="h-3.5 w-3.5"/> Delete
          </button>
        </div>
      </div>
      </div>

      {/* Table */}
      <div className="mt-4 overflow-x-auto rounded-sm border border-zinc-200 bg-white">
        <table className="w-full text-sm min-w-[900px]" data-testid="leads-table">
          <thead className="bg-zinc-50 text-left text-[11px] uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="w-8 px-3 py-3">
                <input type="checkbox" data-testid="select-all-checkbox" checked={selected.size === leads.length && leads.length > 0} onChange={toggleAll} />
              </th>
              <th className="px-3 py-3">Name</th>
              <th className="px-3 py-3">Phone / Email</th>
              <th className="px-3 py-3">Campaign</th>
              <th className="px-3 py-3">Status</th>
              <th className="px-3 py-3">Assigned to</th>
              <th className="px-3 py-3">Sale (₹)</th>
              <th className="px-3 py-3">Quality</th>
              <th className="px-3 py-3">Created</th>
              <th className="px-3 py-3">Updated</th>
            </tr>
          </thead>
          <tbody>
            {leads.length === 0 && (
              <tr><td colSpan={10} className="p-8 text-center text-sm text-zinc-500">No leads yet. Add manually or upload a CSV to get started.</td></tr>
            )}
            {leads.map((l) => (
              <tr key={l.id} className="border-t border-zinc-200 hover:bg-zinc-50 cursor-pointer" onClick={() => setDetailLead(l)}>
                <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" data-testid={`select-lead-${l.id}`} checked={selected.has(l.id)} onChange={() => toggle(l.id)} />
                </td>
                <td className="px-3 py-2.5 font-medium text-black">{l.name || "—"}</td>
                <td className="px-3 py-2.5 text-zinc-700">
                  <div className="tabular-nums">{l.phone}</div>
                  {l.email && <div className="text-[11px] text-zinc-500">{l.email}</div>}
                </td>
                <td className="px-3 py-2.5 text-zinc-700">{l.campaign || l.ad_name || "—"}</td>
                <td className="px-3 py-2.5"><StatusBadge status={l.status} /></td>
                <td className="px-3 py-2.5 text-zinc-700">{l.assigned_to_name || <span className="text-zinc-400">Unassigned</span>}</td>
                <td className="px-3 py-2.5 tabular-nums font-medium">{l.sale_amount ? `₹${l.sale_amount.toLocaleString("en-IN")}` : "—"}</td>
                <td className="px-3 py-2.5">
                  {l.quality_signal ? <span className="inline-flex items-center gap-1 rounded-sm bg-emerald-50 px-1.5 py-0.5 text-[11px] font-medium text-emerald-700"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500"/>Signaled</span> : <span className="text-xs text-zinc-400">—</span>}
                </td>
                <td className="px-3 py-2.5 text-[11px] tabular-nums text-zinc-500" title={formatDate(l.created_at)}>{relTime(l.created_at)}</td>
                <td className="px-3 py-2.5 text-[11px] tabular-nums text-zinc-500" title={formatDate(l.updated_at)}>{relTime(l.updated_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && <AddLeadDialog telecallers={telecallers} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {detailLead && <LeadDetail lead={detailLead} onClose={() => setDetailLead(null)} onSaved={() => { setDetailLead(null); load(); }}
                                 onDelete={() => setDeleteTarget({ ids: [detailLead.id], label: detailLead.name || detailLead.phone })} />}
      {deleteTarget && <ConfirmDeleteDialog target={deleteTarget} onCancel={() => setDeleteTarget(null)} onConfirm={confirmDelete} />}
    </div>
  );
}

function ConfirmDeleteDialog({ target, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-sm border border-zinc-200 bg-white p-6" data-testid="confirm-delete-dialog">
        <div className="mb-4 flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm bg-red-50 text-red-600">
            <AlertTriangle className="h-5 w-5"/>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-red-600">Delete</div>
            <h3 className="font-heading text-lg font-bold text-black">Are you sure you want to delete this lead?</h3>
            <p className="mt-1 text-sm text-zinc-600">This action cannot be undone. <span className="font-medium text-zinc-800">{target.label}</span> will be permanently removed from all lists.</p>
          </div>
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button data-testid="confirm-delete-cancel" onClick={onCancel} className="rounded-sm border border-zinc-300 bg-white px-3 py-2 text-xs font-medium hover:bg-zinc-50">Cancel</button>
          <button data-testid="confirm-delete-yes" onClick={onConfirm} className="inline-flex items-center gap-1.5 rounded-sm bg-red-600 px-3 py-2 text-xs font-semibold text-white hover:bg-red-700">
            <Trash2 className="h-3.5 w-3.5"/> Delete permanently
          </button>
        </div>
      </div>
    </div>
  );
}

function AddLeadDialog({ telecallers, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", lead_id: "", campaign: "", ad_name: "", message: "", assigned_to: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/leads", { ...form, assigned_to: form.assigned_to || null });
      toast.success("Lead added");
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form onSubmit={submit} className="w-full max-w-md rounded-sm border border-zinc-200 bg-white p-6 max-h-[90vh] overflow-auto">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-zinc-500">New</div>
            <h3 className="font-heading text-xl font-bold text-black">Add Lead</h3>
          </div>
          <button type="button" onClick={onClose} className="rounded-sm p-1 hover:bg-zinc-100"><X className="h-4 w-4"/></button>
        </div>
        {[
          {k:"name", label:"Name", req:true},
          {k:"phone", label:"Phone", req:true},
          {k:"email", label:"Email"},
          {k:"lead_id", label:"Meta Lead ID (15-17 digits, optional)"},
          {k:"campaign", label:"Campaign"},
          {k:"ad_name", label:"Ad name"},
          {k:"message", label:"Message"},
        ].map((f) => (
          <div key={f.k} className="mb-3">
            <label className="mb-1 block text-[11px] uppercase tracking-wider text-zinc-500">{f.label}</label>
            <input data-testid={`add-lead-${f.k}`} required={!!f.req} value={form[f.k]} onChange={(e) => setForm({...form, [f.k]: e.target.value})}
              className="w-full rounded-sm border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-black" />
          </div>
        ))}
        <div className="mb-4">
          <label className="mb-1 block text-[11px] uppercase tracking-wider text-zinc-500">Assign to</label>
          <select data-testid="add-lead-assigned" value={form.assigned_to} onChange={(e) => setForm({...form, assigned_to: e.target.value})}
            className="w-full rounded-sm border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-black">
            <option value="">— Unassigned —</option>
            {telecallers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <button data-testid="add-lead-submit" type="submit" className="w-full rounded-sm bg-black px-4 py-2.5 text-sm font-semibold text-white hover:bg-zinc-800">Save Lead</button>
      </form>
    </div>
  );
}

function LeadDetail({ lead, onClose, onSaved, onDelete }) {
  const [name, setName] = useState(lead.name || "");
  const [phone, setPhone] = useState(lead.phone || "");
  const [email, setEmail] = useState(lead.email || "");
  const [status, setStatus] = useState(lead.status);
  const [notes, setNotes] = useState(lead.notes || "");
  const [sale, setSale] = useState(lead.sale_amount || 0);

  const save = async () => {
    try {
      await api.patch(`/leads/${lead.id}`, { name, phone, email, status, notes, sale_amount: parseFloat(sale) || 0 });
      toast.success("Lead updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg rounded-sm border border-zinc-200 bg-white p-6 max-h-[90vh] overflow-auto">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-zinc-500">Lead detail</div>
            <h3 className="font-heading text-xl font-bold text-black">{lead.name}</h3>
            <div className="text-sm text-zinc-500">{lead.phone}{lead.email && ` · ${lead.email}`} · {lead.campaign || lead.ad_name || "No campaign"}</div>
            {lead.lead_id && <div className="text-[11px] tabular-nums text-zinc-500">Meta Lead ID: <code className="text-zinc-700">{lead.lead_id}</code></div>}
            <div className="mt-1 text-[11px] text-zinc-400">
              Created {formatDate(lead.created_at)} · Updated {formatDate(lead.updated_at)}
              {lead.assigned_at && ` · Assigned ${formatDate(lead.assigned_at)}`}
              {lead.completed_at && ` · Completed ${formatDate(lead.completed_at)}`}
            </div>
          </div>
          <button onClick={onClose} className="rounded-sm p-1 hover:bg-zinc-100"><X className="h-4 w-4"/></button>
        </div>

        {lead.message && <div className="mb-4 rounded-sm border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-700"><span className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">WhatsApp message</span>{lead.message}</div>}

        <div className="mb-3 grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-[11px] uppercase tracking-wider text-zinc-500">Name</label>
            <input data-testid="detail-name" value={name} onChange={(e) => setName(e.target.value)}
              className="w-full rounded-sm border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-black" />
          </div>
          <div>
            <label className="mb-1 block text-[11px] uppercase tracking-wider text-zinc-500">Phone</label>
            <input data-testid="detail-phone" value={phone} onChange={(e) => setPhone(e.target.value)}
              className="w-full rounded-sm border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-black" />
          </div>
        </div>
        <div className="mb-3">
          <label className="mb-1 block text-[11px] uppercase tracking-wider text-zinc-500">Email</label>
          <input data-testid="detail-email" value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-sm border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-black" />
        </div>

        <div className="mb-3">
          <label className="mb-1 block text-[11px] uppercase tracking-wider text-zinc-500">Status</label>
          <select data-testid="detail-status" value={status} onChange={(e) => setStatus(e.target.value)}
            className="w-full rounded-sm border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-black">
            <option value="new">New</option>
            <option value="not_connected">Not Connected</option>
            <option value="completed">Completed</option>
            <option value="hold">Hold</option>
            <option value="rejected">Rejected</option>
            <option value="follow_up">Follow-up</option>
          </select>
        </div>

        {status === "completed" && (
          <div className="mb-3">
            <label className="mb-1 block text-[11px] uppercase tracking-wider text-zinc-500">Sale Amount (₹)</label>
            <input data-testid="detail-sale" type="number" value={sale} onChange={(e) => setSale(e.target.value)}
              className="w-full rounded-sm border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-black" />
          </div>
        )}

        <div className="mb-4">
          <label className="mb-1 block text-[11px] uppercase tracking-wider text-zinc-500">Notes</label>
          <textarea data-testid="detail-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
            className="w-full rounded-sm border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-black" />
        </div>

        <button data-testid="detail-save" onClick={save} className="w-full rounded-sm bg-black px-4 py-2.5 text-sm font-semibold text-white hover:bg-zinc-800">
          Save Changes
        </button>

        <button data-testid="detail-delete" onClick={onDelete}
          className="mt-2 w-full inline-flex items-center justify-center gap-1.5 rounded-sm border border-red-500 bg-white px-4 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50">
          <Trash2 className="h-4 w-4"/> Delete this lead
        </button>

        {lead.history?.length > 0 && (
          <div className="mt-5 border-t border-zinc-200 pt-4">
            <div className="mb-2 text-[11px] uppercase tracking-wider text-zinc-500">History</div>
            <div className="space-y-1.5">
              {lead.history.slice().reverse().map((h, i) => (
                <div key={i} className="flex gap-2 text-xs">
                  <span className="text-zinc-400 tabular-nums">{new Date(h.at).toLocaleString("en-IN", { hour12: false })}</span>
                  <span className="text-zinc-700"><b>{h.by}</b> · {h.action}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
